/**
 * Speaker profiles -- group 01 (mainstream high-end floorstanders and monitors).
 *
 * The transducer is where most of the audible difference in a chain lives, so
 * these eight are separated on the axes that actually matter to the model:
 * radiator technology (soft dome, diamond dome, beryllium dome, coaxial
 * point source, air-motion transformer, hybrid dome-plus-ribbon), sensitivity
 * and therefore how much of the upstream noise floor becomes audible, minimum
 * impedance and therefore how hard the amplifier is loaded, bass alignment and
 * therefore group delay and room coupling, and the authored voicing tilt, which
 * is the part that carries each manufacturer's house response.
 *
 * All eight of these designs are reflex-loaded, which is simply what this class
 * of product is; the differentiation is carried by tuning, f3/f6 ratio,
 * crossover topology, directivity and voicing rather than by pretending one of
 * them is sealed.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { makeSpeaker, usd } from '../kit';

export const SPEAKERS_01: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'Wilson Audio Specialties',
    model: 'Sasha DAW',
    price: usd(37900, 'APPROXIMATE', 2020, 'Price per pair at introduction.'),
    year: 2018,
    country: 'United States',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 91,
    nominalOhms: 4,
    minOhms: 2.5,
    bass: [20, 17],
    hfExtension: 30000,
    portTuningHz: 24,
    crossovers: [
      { freqHz: 150, slopeDbOct: 12, alignment: 'butterworth' },
      { freqHz: 2200, slopeDbOct: 12, alignment: 'butterworth', offsetMm: 45 },
    ],
    responseTilt: [[20, 0], [45, 0.4], [120, -0.3], [400, -0.5], [1200, 0.2], [3000, 0.8], [6000, 0.5], [10000, 0.3], [20000, -0.8]],
    resonances: [[95, -40, 16], [260, -44, 20]],
    coverageAngleDeg: 100,
    recommendedWatts: [20, 400],
    dimensionsMm: [1194, 356, 559],
    massKg: 107,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#4f5b66',
    tags: ['modular-enclosure', 'time-aligned', 'difficult-load', 'x-material'],
    notes:
      'The upper-frequency module is a physically separate enclosure whose fore-aft position is set for the listening distance, so the acoustic offset between midrange and tweeter is an adjustable design parameter rather than a fixed baffle geometry -- the profile carries that as an explicit offset on the upper crossover section. Electrically the interesting number is the 2.5 ohm minimum: a 91 dB sensitivity invites low-power amplifiers, but the impedance minimum sits where the music has energy, so a high-output-impedance amplifier will show a clearly audible response error on this load. The bass module is reflex-loaded and tuned low, giving a fourth-order rolloff and correspondingly high group delay below 40 Hz.',
    provenanceNote:
      'Sensitivity, minimum impedance, bass extension and physical dimensions follow Wilson Audio published specifications. Crossover frequencies, slopes and the driver offset are ESTIMATED -- Wilson does not publish them -- and the voicing tilt is an authored MODELLED approximation of the design intent, not a measurement.',
  }),

  makeSpeaker({
    mfr: 'Bowers & Wilkins',
    model: '805 D4',
    price: usd(8000, 'APPROXIMATE', 2022, 'Price per pair.'),
    year: 2021,
    country: 'United Kingdom',
    speakerType: 'standmount',
    enclosure: 'ported',
    drivers: ['diamond-dome', 'dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 88,
    nominalOhms: 8,
    minOhms: 4.6,
    bass: [42, 34],
    hfExtension: 35000,
    portTuningHz: 40,
    crossovers: [[4100, 12]],
    responseTilt: [[20, 0], [60, 0.5], [150, -0.4], [700, -0.2], [2500, -0.8], [4000, 0.4], [8000, 0.6], [14000, 1.2], [20000, 0.4]],
    resonances: [[210, -42, 15], [520, -46, 19]],
    coverageAngleDeg: 120,
    recommendedWatts: [50, 120],
    dimensionsMm: [440, 238, 373],
    massKg: 16,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b9c3cc',
    tags: ['diamond-tweeter', 'continuum-cone', 'solid-body', 'two-way'],
    notes:
      'A two-way with a single crossover placed unusually high at around 4 kHz, which keeps the whole vocal band on one radiator and puts the crossover phase disturbance above the region the ear uses for image placement. The diamond dome pushes its first breakup mode well past 30 kHz instead of into the top octave, so the modelled response keeps rising gently to 14 kHz rather than showing the resonant peak-then-collapse of an aluminium dome. The tapered solid-body tweeter housing and the machined enclosure move cabinet resonances up in frequency and down in level, which is why this profile carries higher-frequency and lower-amplitude cabinet modes than the thin-wall designs elsewhere in the database.',
    provenanceNote:
      'Sensitivity, nominal and minimum impedance, crossover frequency, bass extension, dimensions and mass are Bowers & Wilkins published specifications. Cabinet resonance frequencies and levels are MODELLED from the stated construction, and the voicing tilt is authored rather than measured.',
  }),

  makeSpeaker({
    mfr: 'Focal',
    model: 'Sopra No2',
    price: usd(15000, 'APPROXIMATE', 2020, 'Price per pair; finish options change the figure.'),
    year: 2015,
    country: 'France',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['beryllium-dome', 'dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 91,
    nominalOhms: 8,
    minOhms: 3.4,
    bass: [34, 28],
    hfExtension: 40000,
    portTuningHz: 32,
    crossovers: [[250, 12], [2200, 12]],
    responseTilt: [[20, 0], [50, 0.3], [150, 0.2], [600, -0.3], [1800, -0.9], [4000, 0.9], [7000, 1.3], [12000, 0.6], [20000, 1.0]],
    resonances: [[160, -38, 13], [430, -43, 18]],
    coverageAngleDeg: 95,
    recommendedWatts: [40, 300],
    dimensionsMm: [1230, 358, 540],
    massKg: 55,
    thdPercent: 0.28,
    roomCoupling: 'medium',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c7a882',
    tags: ['beryllium', 'inverted-dome', 'w-cone', 'tuned-mass-damper'],
    notes:
      'The inverted beryllium dome is driven at its rim rather than at its apex, which keeps the dome in pistonic motion far higher than a conventional dome of the same mass and is why the profile is given a 40 kHz upper limit and a top octave that rises rather than rolls. Beryllium breakup, when it arrives, is abrupt and high-Q, so the voicing tilt carries deliberate energy through the 4 to 8 kHz region -- this is a genuinely forward-sounding treble and the model should not flatter it. The midrange enclosure uses a tuned mass damper on the driver basket, modelled here as lower-level cabinet modes than the bass enclosure carries.',
    provenanceNote:
      'Sensitivity, nominal and minimum impedance, frequency extension, dimensions and mass are Focal published specifications. Crossover points are ESTIMATED from the three-way arrangement; the voicing tilt and cabinet resonances are authored MODELLED values.',
  }),

  makeSpeaker({
    mfr: 'KEF',
    model: 'LS50 Meta',
    price: usd(1599, 'CURRENT_RETAIL', 2023, 'Price per pair.'),
    year: 2020,
    country: 'United Kingdom',
    speakerType: 'coaxial',
    enclosure: 'ported',
    drivers: ['coaxial-point-source'],
    driverCount: 1,
    wayCount: 2,
    sensitivityDb: 85,
    nominalOhms: 8,
    minOhms: 3.5,
    bass: [79, 47],
    hfExtension: 28000,
    portTuningHz: 55,
    crossovers: [[2100, 24]],
    responseTilt: [[20, 0], [60, -0.5], [200, 0.3], [800, 0.1], [1800, 0.4], [3000, -0.6], [6000, -0.3], [10000, 0.4], [20000, -1.0]],
    resonances: [[380, -44, 17], [900, -48, 22]],
    coverageAngleDeg: 110,
    recommendedWatts: [40, 100],
    dimensionsMm: [302, 200, 281],
    massKg: 8,
    thdPercent: 0.35,
    roomCoupling: 'medium',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#6ba3c4',
    tags: ['uni-q', 'coaxial', 'metamaterial-absorber', 'point-source'],
    notes:
      'The tweeter sits at the acoustic centre of the midbass cone, so the two drivers share an axis and the crossover region does not produce the vertical lobing a spaced two-way does -- the directivity model therefore keeps a wide, uniform pattern through 2.1 kHz instead of narrowing and reopening. The metamaterial absorber behind the dome is a labyrinth tuned to swallow the tweeter rear wave, which the profile expresses as unusually low-level, high-frequency enclosure modes. At 85 dB sensitivity this is the least efficient speaker in the group by several decibels, which makes upstream noise and amplifier power a real constraint rather than a theoretical one.',
    provenanceNote:
      'Sensitivity, nominal and minimum impedance, the -3 dB and -6 dB bass points, crossover frequency, upper limit, dimensions and mass are all KEF published specifications; KEF publishes an unusually complete set for this model. The voicing tilt and the enclosure mode values are authored MODELLED approximations.',
  }),

  makeSpeaker({
    mfr: 'Sonus Faber',
    model: 'Olympica Nova III',
    price: usd(16000, 'APPROXIMATE', 2021, 'Price per pair.'),
    year: 2019,
    country: 'Italy',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 90,
    nominalOhms: 4,
    minOhms: 3.2,
    bass: [35, 31],
    hfExtension: 26000,
    portTuningHz: 33,
    crossovers: [
      { freqHz: 270, slopeDbOct: 12, alignment: 'butterworth' },
      { freqHz: 2500, slopeDbOct: 12, alignment: 'butterworth' },
    ],
    responseTilt: [[20, 0], [70, 0.8], [200, 0.6], [600, 0.1], [1500, -0.3], [3000, -0.6], [6000, -1.0], [10000, -1.4], [20000, -2.0]],
    resonances: [[120, -30, 8], [340, -34, 11], [700, -39, 14]],
    coverageAngleDeg: 115,
    recommendedWatts: [30, 300],
    dimensionsMm: [1120, 400, 470],
    massKg: 40,
    thdPercent: 0.32,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#8a5a3c',
    tags: ['lute-shape', 'silk-dome', 'bottom-vent', 'downward-tilt'],
    notes:
      'The house voicing is the point of this entry: a progressive downward tilt from the presence region upward, of the order of two decibels by 20 kHz, sitting on a small lower-midrange lift. That is a real and deliberate response shape, not an adjective, and it is the single largest difference between this profile and the Focal one, which tilts the opposite way over the same span. The curved multi-layer wooden shell has no parallel internal surfaces but is not inert, so the model gives it three low-Q modes spread through the lower midrange, closer in character to the BBC thin-wall approach than to a machined metal box. The bottom-firing reflex vent fires into the plinth gap, which raises the effective coupling to floor boundary loading.',
    provenanceNote:
      'Sensitivity, nominal impedance, frequency range, dimensions and mass follow Sonus Faber published specifications. Minimum impedance and crossover frequencies are ESTIMATED; the voicing tilt and the cabinet mode set are authored MODELLED values chosen to represent the design intent rather than a measurement of a unit.',
  }),

  makeSpeaker({
    mfr: 'Dynaudio',
    model: 'Focus 50',
    price: usd(11000, 'APPROXIMATE', 2023, 'Price per pair; finish options change the figure.'),
    year: 2022,
    country: 'Denmark',
    speakerType: 'active',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 4,
    wayCount: 3,
    active: true,
    sensitivityDb: 88,
    nominalOhms: 8,
    minOhms: 6.5,
    bass: [33, 27],
    hfExtension: 25000,
    portTuningHz: 31,
    crossovers: [
      { freqHz: 350, slopeDbOct: 48, alignment: 'digital-lr' },
      { freqHz: 4300, slopeDbOct: 48, alignment: 'digital-lr' },
    ],
    responseTilt: [[20, 0], [50, 0.2], [150, -0.2], [500, -0.3], [1500, -0.5], [3000, -0.7], [6000, -0.6], [12000, -0.9], [20000, -1.2]],
    resonances: [[150, -45, 18], [360, -48, 22]],
    coverageAngleDeg: 105,
    dimensionsMm: [1105, 205, 350],
    massKg: 26,
    thdPercent: 0.25,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#5c8a72',
    tags: ['active', 'dsp-crossover', 'esotar-tweeter', 'streaming'],
    notes:
      'Each driver has its own amplifier and its own DSP filter, so the crossover is a 48 dB per octave digital Linkwitz-Riley rather than a passive network -- steep enough that driver overlap is essentially eliminated, at the cost of a much larger absolute group delay through the crossover regions than any of the passive designs here. Because the amplifiers are inside the cabinet and directly coupled to known loads, the compression term is smaller than a passive speaker of the same size, and the electrical load presented to the rest of the chain is a line input rather than a loudspeaker impedance. That last point is why the impedance and sensitivity fields on this entry are placeholders rather than physical claims.',
    provenanceNote:
      'The active three-way arrangement, DSP crossover and driver complement follow Dynaudio published material. Sensitivity and impedance are NOT meaningful published figures for an active speaker; the values here are placeholders that let the chain model do level matching, and they must not be read as measurements. Crossover frequencies, bass extension and the voicing tilt are ESTIMATED or MODELLED.',
  }),

  makeSpeaker({
    mfr: 'DALI',
    model: 'Epicon 6',
    price: usd(14000, 'APPROXIMATE', 2021, 'Price per pair.'),
    year: 2012,
    country: 'Denmark',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic', 'ribbon'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 88,
    nominalOhms: 4,
    minOhms: 3.3,
    bass: [37, 31],
    hfExtension: 32000,
    portTuningHz: 35,
    crossovers: [[700, 12], [2600, 12], [14000, 6]],
    responseTilt: [[20, 0], [60, 0.7], [180, 0.3], [700, -0.2], [2000, -0.5], [4000, -0.4], [9000, 0.2], [15000, 1.0], [20000, 1.4]],
    resonances: [[140, -36, 12], [390, -41, 16]],
    coverageAngleDeg: 105,
    recommendedWatts: [50, 400],
    dimensionsMm: [1100, 240, 425],
    massKg: 32,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9c6b4f',
    tags: ['hybrid-tweeter', 'ribbon-supertweeter', 'smc-magnet', 'wood-fibre-cone'],
    notes:
      'The tweeter module is two radiators rather than one: a soft dome carries the treble and a planar ribbon is crossed in above roughly 14 kHz with a shallow first-order slope. The audible and modelled consequences are a third crossover section high in the band and a directivity that widens rather than narrows in the top octave, because the ribbon is a short line source horizontally. The soft magnetic composite pole pieces reduce the modulation of the magnetic circuit by the voice-coil current, which shows up as lower third-harmonic distortion in the midrange than a conventional motor of the same size, so this entry is given a distortion figure weighted toward the second harmonic.',
    provenanceNote:
      'Sensitivity, nominal impedance, crossover frequencies including the supertweeter transition, frequency range, dimensions and mass follow DALI published specifications. Minimum impedance is ESTIMATED; the voicing tilt, distortion structure and cabinet modes are MODELLED.',
  }),

  makeSpeaker({
    mfr: 'Monitor Audio',
    model: 'Platinum 200 3G',
    price: usd(12000, 'APPROXIMATE', 2022, 'Price per pair.'),
    year: 2021,
    country: 'United Kingdom',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['amt', 'dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 88,
    nominalOhms: 8,
    minOhms: 3.2,
    bass: [30, 25],
    hfExtension: 60000,
    portTuningHz: 29,
    crossovers: [[700, 24], [3400, 24]],
    responseTilt: [[20, 0], [55, 0.3], [160, -0.3], [600, 0.2], [1500, -0.7], [3500, 0.5], [8000, 0.8], [16000, 0.9], [30000, 0.5]],
    resonances: [[175, -41, 15], [450, -45, 19]],
    coverageAngleDeg: 100,
    recommendedWatts: [100, 300],
    dimensionsMm: [1046, 380, 350],
    massKg: 30,
    thdPercent: 0.26,
    roomCoupling: 'medium',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a8a29a',
    tags: ['amt', 'folded-ribbon', 'ceramic-coated-cone', 'ultra-wideband'],
    notes:
      'The high-frequency unit is a folded pleated diaphragm, which moves air by squeezing the folds together rather than by pistonic excursion; the effective radiating area is large while the moving mass stays tiny, so useful output continues far past the audio band and the profile is given a 60 kHz upper limit rather than the 25 to 30 kHz of the dome designs here. That matters to the model in two ways: the reconstruction images from a non-oversampling source upstream are actually reproduced instead of being filtered away by the transducer, and the vertical directivity narrows earlier than a dome because the diaphragm is tall. The ceramic-coated aluminium bass cones push their breakup modes above the crossover, so the fourth-order crossover slopes are doing real work suppressing them.',
    provenanceNote:
      'Sensitivity, nominal and minimum impedance, crossover frequencies, the stated frequency range including the ultrasonic extension, dimensions and mass are Monitor Audio published specifications. The voicing tilt, cabinet modes and distortion structure are authored MODELLED values.',
  }),
];
